import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

// Функция для получения ID видео из URL
const extractVideoId = (url, source = 'youtube') => {
  if (!url || typeof url !== 'string') {
    console.warn('extractVideoId: URL пустой или не является строкой');
    return null;
  }
  
  try {
    // Проверяем, не является ли URL уже встроенным
    if (source === 'youtube') {
      if (url.includes('youtube.com/embed/')) {
        return url.split('/').pop().split('?')[0];
      }
      
      // YouTube URL patterns
      const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
      const match = url.match(youtubeRegex);
      return match && match[1] ? match[1] : null;
    } else if (source === 'vimeo') {
      if (url.includes('player.vimeo.com/video/')) {
        return url.split('/').pop().split('?')[0];
      }
      
      // Vimeo URL patterns
      const vimeoRegex = /(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_-]+)?)/i;
      const match = url.match(vimeoRegex);
      return match && match[1] ? match[1] : null;
    } else if (source === 'rutube' || (source === 'other' && url.includes('rutube.ru'))) {
      // Rutube URL patterns
      // Поддерживаем форматы:
      // https://rutube.ru/video/[ID]/
      // https://rutube.ru/shorts/[ID]/
      // https://rutube.ru/play/embed/[ID]
      
      if (url.includes('rutube.ru/play/embed/')) {
        return url.split('/').pop().split('?')[0];
      }
      
      const rutubeRegex = /(?:rutube\.ru\/(?:video|shorts)\/([a-zA-Z0-9]+))/i;
      const match = url.match(rutubeRegex);
      return match && match[1] ? match[1] : null;
    } else if (source === 'other') {
      // Для других источников просто возвращаем весь URL как ID
      // Это позволит использовать URL как есть для встраивания
      console.log(`extractVideoId: Используем полный URL как ID для источника 'other': ${url}`);
      return url;
    }
    
    return null;
  } catch (error) {
    console.error(`extractVideoId: Ошибка при обработке URL: ${url}`, error);
    return null;
  }
};

// Функция для генерации URL для встраивания видео
const generateEmbedUrl = (videoId, source = 'youtube') => {
  if (!videoId) {
    console.warn('generateEmbedUrl: videoId не указан');
    return null;
  }
  
  try {
    if (source === 'youtube') {
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (source === 'vimeo') {
      return `https://player.vimeo.com/video/${videoId}`;
    } else if (source === 'rutube') {
      // Формат встраивания для Rutube
      return `https://rutube.ru/play/embed/${videoId}`;
    } else if (source === 'other') {
      // Для других источников, если videoId является полным URL
      
      // Проверяем, если это URL Rutube
      if (typeof videoId === 'string' && videoId.includes('rutube.ru')) {
        if (videoId.includes('rutube.ru/play/embed/')) {
          // Уже является встроенным URL
          return videoId;
        } else {
          // Пытаемся извлечь ID из URL Rutube
          const rutubeRegex = /(?:rutube\.ru\/(?:video|shorts)\/([a-zA-Z0-9]+))/i;
          const match = videoId.match(rutubeRegex);
          if (match && match[1]) {
            return `https://rutube.ru/play/embed/${match[1]}`;
          }
        }
      }
      
      // Если это полный URL с http/https, используем его как есть
      if (typeof videoId === 'string' && (videoId.startsWith('http://') || videoId.startsWith('https://'))) {
        console.log(`generateEmbedUrl: Используем полный URL как есть: ${videoId}`);
        return videoId;
      }
    }
    
    console.warn(`generateEmbedUrl: Неподдерживаемый источник: ${source}`);
    return null;
  } catch (error) {
    console.error(`generateEmbedUrl: Ошибка при генерации URL для встраивания: ${videoId}`, error);
    return null;
  }
};

// Функция для генерации URL превью видео
const generateThumbnailUrl = (videoId, source = 'youtube') => {
  if (!videoId) {
    console.warn('generateThumbnailUrl: videoId не указан');
    return null;
  }
  
  // Заглушка по умолчанию для всех источников, если не удастся получить превью
  const defaultThumbnail = '/assets/placeholders/no-video-preview.jpg';
  
  try {
    if (source === 'youtube') {
      // Для YouTube есть прямой доступ к превью
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    } else if (source === 'vimeo') {
      // Для Vimeo нужен API для получения превью
      return defaultThumbnail;
    } else if (source === 'rutube') {
      // Для Rutube нет прямого доступа к превью без API
      return defaultThumbnail;
    } else if (source === 'other') {
      // Проверяем, если это URL Rutube
      if (typeof videoId === 'string' && videoId.includes('rutube.ru')) {
        // Для Rutube используем заглушку
        return defaultThumbnail;
      }
      
      // Для других источников используем заглушку
      console.log(`generateThumbnailUrl: Используем заглушку для источника 'other'`);
      return defaultThumbnail;
    }
    
    console.warn(`generateThumbnailUrl: Неподдерживаемый источник: ${source}`);
    return defaultThumbnail;
  } catch (error) {
    console.error(`generateThumbnailUrl: Ошибка при генерации URL превью: ${videoId}`, error);
    return defaultThumbnail;
  }
};

const VideoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuthStore();
  const isEditMode = Boolean(id);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    source: 'youtube',
    thumbnailUrl: '',
    category: '',
    tags: [],
    isActive: true
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [previewData, setPreviewData] = useState({
    embedUrl: '',
    thumbnailUrl: ''
  });
  
  // Fetch video data if in edit mode
  useEffect(() => {
    fetchCategories();
    
    // Проверяем, что мы в режиме редактирования и id определен
    if (isEditMode && id) {
      fetchVideoData();
    }
  }, [isEditMode, id]);
  
  // Fetch video data
  const fetchVideoData = async () => {
    try {
      // Проверяем, что ID определен и валиден
      if (!id || id === 'undefined' || id === 'null' || id === undefined || id === null) {
        console.error('Недопустимый ID видео:', id);
        setError('Недопустимый ID видео');
        setLoading(false);
        return;
      }
      
      console.log('Загрузка данных видео с ID:', id);
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log(`Запрашиваем видео с ID: ${id}`);
      
      const response = await axios.get(`/api/videos/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const videoData = response.data.data;
      console.log('Получены данные видео:', videoData);
      
      // Проверяем наличие URL в полученных данных
      if (!videoData.url) {
        console.warn('Видео не содержит URL!');
      }
      
      setFormData({
        title: videoData.title || '',
        description: videoData.description || '',
        url: videoData.url || '',
        source: videoData.source || 'youtube',
        thumbnailUrl: videoData.thumbnailUrl || '',
        category: videoData.category || '',
        tags: videoData.tags || [],
        isActive: videoData.isActive !== undefined ? videoData.isActive : true
      });
      
      setPreviewData({
        embedUrl: videoData.embedUrl || '',
        thumbnailUrl: videoData.thumbnailUrl || ''
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching video data:', error);
      setError('Ошибка при загрузке данных видео');
      setLoading(false);
    }
  };
  
  // Fetch categories
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/videos/categories', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'url') {
      // When URL changes, try to extract video ID and generate embed URL
      const source = formData.source;
      const videoId = extractVideoId(value, source);
      const embedUrl = generateEmbedUrl(videoId, source);
      const thumbnailUrl = generateThumbnailUrl(videoId, source);
      
      setPreviewData({
        embedUrl,
        thumbnailUrl
      });
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Update preview for URL changes
    if (name === 'url' || name === 'source') {
      // Проверяем наличие URL перед обновлением превью
      const urlToUse = name === 'url' ? value : formData.url;
      const sourceToUse = name === 'source' ? value : formData.source;
      
      if (!urlToUse) {
        console.warn('Невозможно обновить превью: URL не указан');
        return;
      }
      
      console.log('Обновление превью для:', { url: urlToUse, source: sourceToUse });
      updatePreview(urlToUse, sourceToUse);
    }
  };
  
  // Update preview data
  const updatePreview = (url, source) => {
    if (!url || typeof url !== 'string') {
      console.warn('updatePreview: URL пустой или не является строкой');
      return;
    }
    
    try {
      console.log(`updatePreview: Обработка URL: ${url}, источник: ${source}`);
      
      // Проверяем, не является ли URL уже встроенным
      let embedUrl = null;
      let videoId = null;
      
      // Проверяем различные типы встроенных URL
      if (url.includes('youtube.com/embed/') || 
          url.includes('player.vimeo.com/video/') || 
          url.includes('rutube.ru/play/embed/')) {
        console.log(`updatePreview: URL уже является встроенным: ${url}`);
        embedUrl = url;
        // Извлекаем ID из встроенного URL
        videoId = url.split('/').pop().split('?')[0];
      } else {
        // Обычный URL, извлекаем ID и генерируем embedUrl
        videoId = extractVideoId(url, source);
        
        if (!videoId) {
          console.warn(`updatePreview: Не удалось извлечь ID видео из URL: ${url}`);
          
          // Для источника 'other' используем сам URL как ID
          if (source === 'other') {
            console.log(`updatePreview: Используем полный URL как ID для источника 'other': ${url}`);
            videoId = url;
          } else {
            return; // Для других источников необходим ID
          }
        }
        
        // Генерируем URL для встраивания
        embedUrl = generateEmbedUrl(videoId, source);
        
        if (!embedUrl && source === 'other') {
          // Для источника 'other', если не удалось сгенерировать embedUrl,
          // используем сам URL если он начинается с http/https
          if (url.startsWith('http://') || url.startsWith('https://')) {
            console.log(`updatePreview: Используем полный URL как embedUrl: ${url}`);
            embedUrl = url;
          }
        }
      }
      
      // Если не удалось сгенерировать embedUrl, прекращаем обработку
      if (!embedUrl) {
        console.warn(`updatePreview: Не удалось сгенерировать embedUrl для URL: ${url}`);
        return;
      }
      
      // Генерируем URL превью
      const thumbnailUrl = generateThumbnailUrl(videoId, source);
      
      console.log('updatePreview: Сгенерированы данные превью:', {
        videoId,
        embedUrl,
        thumbnailUrl
      });
      
      setPreviewData({
        embedUrl,
        thumbnailUrl
      });
    } catch (error) {
      console.error(`updatePreview: Ошибка при обработке URL: ${url}`, error);
    }
  };
  
  // Handle source change
  const handleSourceChange = (e) => {
    const source = e.target.value;
    console.log(`handleSourceChange: Изменен источник на: ${source}`);
    
    setFormData(prev => ({
      ...prev,
      source
    }));
    
    // Проверяем наличие URL перед обновлением превью
    if (!formData.url) {
      console.warn('handleSourceChange: Невозможно обновить превью: URL не указан');
      return;
    }
    
    // Обновляем превью с новым источником
    console.log(`handleSourceChange: Обновление превью для URL: ${formData.url}, источник: ${source}`);
    updatePreview(formData.url, source);
  };
  
  // Handle switch change
  const handleSwitchChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.checked
    }));
  };
  
  // Extract video ID from URL
  const extractVideoId = (url, source) => {
    if (!url) return '';
    
    let videoId = '';
    
    if (source === 'youtube') {
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1];
        const ampersandPosition = videoId?.indexOf('&');
        if (ampersandPosition !== -1) {
          videoId = videoId.substring(0, ampersandPosition);
        }
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1];
        const questionMarkPosition = videoId?.indexOf('?');
        if (questionMarkPosition !== -1) {
          videoId = videoId.substring(0, questionMarkPosition);
        }
      }
    } else if (source === 'vimeo') {
      if (url.includes('vimeo.com/')) {
        videoId = url.split('vimeo.com/')[1];
        const questionMarkPosition = videoId?.indexOf('?');
        if (questionMarkPosition !== -1) {
          videoId = videoId.substring(0, questionMarkPosition);
        }
      }
    }
    
    return videoId;
  };
  
  // Generate embed URL
  const generateEmbedUrl = (videoId, source) => {
    if (!videoId) return '';
    
    if (source === 'youtube') {
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (source === 'vimeo') {
      return `https://player.vimeo.com/video/${videoId}`;
    }
    
    return '';
  };
  
  // Generate thumbnail URL
  const generateThumbnailUrl = (videoId, source) => {
    if (!videoId) return '';
    
    if (source === 'youtube') {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    
    return '';
  };
  
  // Handle tag input
  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
      }
      setTagInput('');
    }
  };
  
  // Remove tag
  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };
  
  // Add new category
  const handleAddCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      setCategories(prev => [...prev, newCategory.trim()]);
      setFormData(prev => ({
        ...prev,
        category: newCategory.trim()
      }));
      setNewCategory('');
    }
  };
  
  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Проверяем обязательные поля
      if (!formData.title) {
        setError('Необходимо указать название видео');
        return;
      }
      
      if (!formData.url) {
        setError('Необходимо указать URL видео');
        return;
      }
      
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Используем thumbnailUrl из предпросмотра, если не указан явно
      const dataToSubmit = {
        ...formData,
        thumbnailUrl: formData.thumbnailUrl || previewData.thumbnailUrl
      };
      
      // Отладочная информация
      console.log('Отправляем данные видео:', dataToSubmit);
      
      let response;
      
      if (isEditMode) {
        // Проверяем, что ID определен
        if (!id || id === 'undefined') {
          throw new Error('Недопустимый ID видео');
        }
        
        response = await axios.put(`/api/videos/${id}`, dataToSubmit, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } else {
        response = await axios.post('/api/videos', dataToSubmit, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      
      console.log('Ответ сервера:', response.data);
      
      setSuccess(true);
      setLoading(false);
      
      // Перенаправление после короткой задержки
      setTimeout(() => {
        navigate('/admin/videos');
      }, 1500);
      
    } catch (error) {
      console.error('Error submitting video:', error);
      setError(error.response?.data?.message || 'Ошибка при сохранении видео');
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/videos')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Редактирование видео' : 'Добавление нового видео'}
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Видео успешно {isEditMode ? 'обновлено' : 'добавлено'}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Основная информация
              </Typography>
              
              <TextField
                fullWidth
                label="Название видео"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Описание"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                margin="normal"
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="source-select-label">Источник видео</InputLabel>
                <Select
                  labelId="source-select-label"
                  id="source-select"
                  name="source"
                  value={formData.source}
                  onChange={handleSourceChange}
                  label="Источник видео"
                >
                  <MenuItem value="youtube">YouTube</MenuItem>
                  <MenuItem value="vimeo">Vimeo</MenuItem>
                  <MenuItem value="rutube">Rutube</MenuItem>
                  <MenuItem value="other">Другой</MenuItem>
                </Select>
                <FormHelperText>
                  Выберите платформу, на которой размещено видео
                </FormHelperText>
              </FormControl>
              
              <TextField
                fullWidth
                label="URL видео"
                name="url"
                value={formData.url}
                onChange={handleChange}
                required
                margin="normal"
                placeholder={
                  formData.source === 'youtube'
                    ? 'https://www.youtube.com/watch?v=VIDEOID или https://youtu.be/VIDEOID'
                    : formData.source === 'vimeo'
                    ? 'https://vimeo.com/VIDEOID'
                    : 'URL видео'
                }
                helperText={
                  formData.source === 'youtube'
                    ? 'Вставьте ссылку на видео с YouTube'
                    : formData.source === 'vimeo'
                    ? 'Вставьте ссылку на видео с Vimeo'
                    : 'Вставьте ссылку на видео'
                }
              />
              
              <TextField
                fullWidth
                label="URL миниатюры (превью)"
                name="thumbnailUrl"
                value={formData.thumbnailUrl}
                onChange={handleChange}
                margin="normal"
                helperText="Оставьте пустым для автоматического получения миниатюры (для YouTube)"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleSwitchChange}
                    name="isActive"
                    color="primary"
                  />
                }
                label="Активно"
              />
            </Paper>
            
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Категоризация
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="category-label">Категория</InputLabel>
                  <Select
                    labelId="category-label"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    label="Категория"
                  >
                    <MenuItem value="">Без категории</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                  <TextField
                    label="Новая категория"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddCategory}
                    disabled={!newCategory}
                  >
                    Добавить
                  </Button>
                </Box>
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                Теги
              </Typography>
              
              <TextField
                fullWidth
                label="Добавить тег"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                margin="normal"
                helperText="Нажмите Enter для добавления тега"
              />
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Paper>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/videos')}
                startIcon={<CancelIcon />}
              >
                Отмена
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {isEditMode ? 'Обновить' : 'Сохранить'}
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Предпросмотр
              </Typography>
              
              {previewData.embedUrl ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Видео:
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      paddingBottom: '56.25%', // 16:9 aspect ratio
                      height: 0,
                      overflow: 'hidden',
                      mb: 2,
                      '& iframe': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }
                    }}
                  >
                    <iframe
                      src={previewData.embedUrl}
                      title="Video preview"
                      allowFullScreen
                    />
                  </Box>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Введите корректный URL видео для предпросмотра
                </Alert>
              )}
              
              {(previewData.thumbnailUrl || formData.thumbnailUrl) && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Миниатюра:
                  </Typography>
                  <Box
                    component="img"
                    src={formData.thumbnailUrl || previewData.thumbnailUrl}
                    alt="Thumbnail preview"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 1
                    }}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default VideoForm;
